// ============================================
// AUDITORIA - ADMIN
// src/pages/admin/AdminAuditoria.tsx
// ============================================

import React, { useState, useEffect, useCallback } from 'react';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { API_CONFIG } from '../../services/staticDataConfig';

interface LogAuditoria {
  id: number;
  admin_id: number;
  admin_nome: string;
  acao: string;
  entidade: string;
  entidade_id: number | null;
  detalhes: string;
  ip: string;
  created_at: string;
}

interface Paginacao {
  pagina: number;
  limite: number;
  total: number;
  totalPaginas: number;
}

interface Filtros {
  busca: string;
  acao: string;
  entidade: string;
  adminId: string;
  dataInicio: string;
  dataFim: string;
}

const ACOES = [
  { valor: '', label: 'Todas' },
  { valor: 'login', label: 'Login' },
  { valor: 'logout', label: 'Logout' },
  { valor: 'create', label: 'Criação' },
  { valor: 'update', label: 'Atualização' },
  { valor: 'delete', label: 'Exclusão' },
  { valor: 'reset_password', label: 'Reset de Senha' },
  { valor: 'status_change', label: 'Alteração de Status' },
  { valor: 'view', label: 'Visualização' },
  { valor: 'export', label: 'Exportação' }
];

const ENTIDADES = [
  { valor: '', label: 'Todas' },
  { valor: 'admin', label: 'Administrador' },
  { valor: 'empresa', label: 'Empresa' },
  { valor: 'usuario', label: 'Usuário' },
  { valor: 'configuracao', label: 'Configuração' },
  { valor: 'sessao', label: 'Sessão' }
];

export function AdminAuditoria() {
  const { token, hasPermission } = useAdminAuth();
  const [logs, setLogs] = useState<LogAuditoria[]>([]);
  const [loading, setLoading] = useState(true);
  const [exportando, setExportando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [paginacao, setPaginacao] = useState<Paginacao>({
    pagina: 1,
    limite: 50,
    total: 0,
    totalPaginas: 0
  });
  const [filtros, setFiltros] = useState<Filtros>({
    busca: '',
    acao: '',
    entidade: '',
    adminId: '',
    dataInicio: '',
    dataFim: ''
  });
  const [logSelecionado, setLogSelecionado] = useState<LogAuditoria | null>(null);
  const [showModal, setShowModal] = useState(false);

  const carregarLogs = useCallback(async () => {
    if (!hasPermission('visualizar_logs')) {
      setErro('Você não tem permissão para visualizar logs de auditoria');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setErro(null);

      const params = new URLSearchParams();
      params.append('pagina', paginacao.pagina.toString());
      params.append('limite', paginacao.limite.toString());
      
      if (filtros.busca) params.append('busca', filtros.busca);
      if (filtros.acao) params.append('acao', filtros.acao);
      if (filtros.entidade) params.append('entidade', filtros.entidade);
      if (filtros.adminId) params.append('adminId', filtros.adminId);
      if (filtros.dataInicio) params.append('dataInicio', filtros.dataInicio);
      if (filtros.dataFim) params.append('dataFim', filtros.dataFim);

      const response = await fetch(`${API_CONFIG.BASE_URL}/admin-sistema/auditoria/logs?${params.toString()}`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Erro ao carregar logs');
      }

      const data = await response.json();
      
      if (data.sucesso) {
        setLogs(data.dados || []);
        setPaginacao(prev => ({
          ...prev,
          total: data.paginacao?.total || 0,
          totalPaginas: data.paginacao?.totalPaginas || 0
        }));
      }
    } catch (error) {
      console.error('[AdminAuditoria] Erro:', error);
      setErro('Erro ao carregar logs de auditoria');
    } finally {
      setLoading(false);
    }
  }, [token, hasPermission, paginacao.pagina, paginacao.limite, filtros]);

  useEffect(() => {
    carregarLogs();
  }, [carregarLogs]);

  async function exportarCSV() {
    if (!hasPermission('exportar_dados')) {
      setErro('Você não tem permissão para exportar dados');
      return;
    }

    try {
      setExportando(true);
      setErro(null);

      const params = new URLSearchParams();
      if (filtros.acao) params.append('acao', filtros.acao);
      if (filtros.entidade) params.append('entidade', filtros.entidade);
      if (filtros.adminId) params.append('adminId', filtros.adminId);
      if (filtros.dataInicio) params.append('dataInicio', filtros.dataInicio);
      if (filtros.dataFim) params.append('dataFim', filtros.dataFim);

      const response = await fetch(`${API_CONFIG.BASE_URL}/admin-sistema/auditoria/exportar?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Erro ao exportar logs');
      }

      // Download do arquivo
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `auditoria_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

    } catch (error) {
      console.error('[AdminAuditoria] Erro ao exportar:', error);
      setErro('Erro ao exportar logs');
    } finally {
      setExportando(false);
    }
  }

  function handleFiltroChange(campo: keyof Filtros, valor: string) {
    setFiltros(prev => ({ ...prev, [campo]: valor }));
    setPaginacao(prev => ({ ...prev, pagina: 1 }));
  }

  function limparFiltros() {
    setFiltros({
      busca: '',
      acao: '',
      entidade: '',
      adminId: '',
      dataInicio: '',
      dataFim: ''
    });
    setPaginacao(prev => ({ ...prev, pagina: 1 }));
  }

  function formatarDataHora(dataString: string): string {
    if (!dataString) return '-';
    try {
      const data = new Date(dataString);
      return data.toLocaleString('pt-BR');
    } catch {
      return dataString;
    }
  }

  function getAcaoCor(acao: string): string {
    const cores: Record<string, string> = {
      'login': 'success',
      'logout': 'secondary',
      'create': 'primary',
      'update': 'warning',
      'delete': 'danger',
      'reset_password': 'info',
      'status_change': 'warning',
      'view': 'light',
      'export': 'info'
    };
    return cores[acao] || 'secondary';
  }

  function getAcaoIcone(acao: string): string {
    const icones: Record<string, string> = {
      'login': 'fa-sign-in-alt',
      'logout': 'fa-sign-out-alt',
      'create': 'fa-plus',
      'update': 'fa-edit',
      'delete': 'fa-trash',
      'reset_password': 'fa-key',
      'status_change': 'fa-toggle-on',
      'view': 'fa-eye',
      'export': 'fa-download'
    };
    return icones[acao] || 'fa-circle';
  }

  function verDetalhes(log: LogAuditoria) {
    setLogSelecionado(log);
    setShowModal(true);
  }

  function formatarDetalhes(detalhes: string): React.ReactNode {
    try {
      const obj = JSON.parse(detalhes);
      return (
        <pre className="bg-light p-3 rounded small mb-0" style={{ maxHeight: '300px', overflow: 'auto' }}>
          {JSON.stringify(obj, null, 2)}
        </pre>
      );
    } catch {
      return <p className="mb-0">{detalhes}</p>;
    }
  }

  return (
    <div className="container-fluid">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-1">
            <i className="fas fa-history me-2"></i>
            Logs de Auditoria
          </h2>
          <p className="text-muted mb-0">
            {paginacao.total} registro{paginacao.total !== 1 ? 's' : ''} encontrado{paginacao.total !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="d-flex gap-2">
          {hasPermission('exportar_dados') && (
            <button 
              className="btn btn-success"
              onClick={exportarCSV}
              disabled={exportando || loading}
            >
              {exportando ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Exportando...
                </>
              ) : (
                <>
                  <i className="fas fa-download me-2"></i>
                  Exportar CSV
                </>
              )}
            </button>
          )}
          <button 
            className="btn btn-outline-primary"
            onClick={carregarLogs}
            disabled={loading}
          >
            <i className={`fas fa-sync-alt ${loading ? 'fa-spin' : ''} me-2`}></i>
            Atualizar
          </button>
        </div>
      </div>

      {/* Alertas */}
      {erro && (
        <div className="alert alert-danger d-flex align-items-center" role="alert">
          <i className="fas fa-exclamation-circle me-2"></i>
          <div>{erro}</div>
          <button type="button" className="btn-close ms-auto" onClick={() => setErro(null)}></button>
        </div>
      )}

      {/* Filtros */}
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-3">
              <label className="form-label">Buscar</label>
              <div className="input-group">
                <span className="input-group-text">
                  <i className="fas fa-search"></i>
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Admin, detalhes, IP..."
                  value={filtros.busca}
                  onChange={(e) => handleFiltroChange('busca', e.target.value)}
                />
              </div>
            </div>
            <div className="col-md-2">
              <label className="form-label">Ação</label>
              <select
                className="form-select"
                value={filtros.acao}
                onChange={(e) => handleFiltroChange('acao', e.target.value)}
              >
                {ACOES.map(a => (
                  <option key={a.valor} value={a.valor}>{a.label}</option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label">Entidade</label>
              <select
                className="form-select"
                value={filtros.entidade}
                onChange={(e) => handleFiltroChange('entidade', e.target.value)}
              >
                {ENTIDADES.map(e => (
                  <option key={e.valor} value={e.valor}>{e.label}</option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label">Data Início</label>
              <input
                type="date"
                className="form-control"
                value={filtros.dataInicio}
                onChange={(e) => handleFiltroChange('dataInicio', e.target.value)}
              />
            </div>
            <div className="col-md-2">
              <label className="form-label">Data Fim</label>
              <input
                type="date"
                className="form-control"
                value={filtros.dataFim}
                onChange={(e) => handleFiltroChange('dataFim', e.target.value)}
              />
            </div>
            <div className="col-md-1 d-flex align-items-end">
              <button 
                className="btn btn-outline-secondary w-100"
                onClick={limparFiltros}
                title="Limpar filtros"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabela de Logs */}
      <div className="card border-0 shadow-sm">
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead className="table-light">
                <tr>
                  <th style={{ width: '60px' }}>ID</th>
                  <th style={{ width: '180px' }}>Data/Hora</th>
                  <th>Administrador</th>
                  <th style={{ width: '130px' }}>Ação</th>
                  <th style={{ width: '130px' }}>Entidade</th>
                  <th>Detalhes</th>
                  <th style={{ width: '130px' }}>IP</th>
                  <th style={{ width: '60px' }}>Ver</th>
                </tr>
              </thead>
              <tbody>
                {loading && logs.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center py-5">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Carregando...</span>
                      </div>
                    </td>
                  </tr>
                ) : logs.length > 0 ? (
                  logs.map((log) => (
                    <tr key={log.id}>
                      <td>
                        <span className="badge bg-light text-dark">{log.id}</span>
                      </td>
                      <td>
                        <small>{formatarDataHora(log.created_at)}</small>
                      </td>
                      <td>
                        <strong>{log.admin_nome || `Admin #${log.admin_id}`}</strong>
                      </td>
                      <td>
                        <span className={`badge bg-${getAcaoCor(log.acao)}`}>
                          <i className={`fas ${getAcaoIcone(log.acao)} me-1`}></i>
                          {log.acao}
                        </span>
                      </td>
                      <td>
                        <span className="badge bg-secondary">
                          {log.entidade}
                          {log.entidade_id && (
                            <span className="ms-1">#{log.entidade_id}</span>
                          )}
                        </span>
                      </td>
                      <td>
                        <small className="text-truncate d-block" style={{ maxWidth: '300px' }}>
                          {log.detalhes || '-'}
                        </small>
                      </td>
                      <td>
                        <code className="small">{log.ip}</code>
                      </td>
                      <td>
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => verDetalhes(log)}
                          title="Ver detalhes"
                        >
                          <i className="fas fa-eye"></i>
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={8} className="text-center py-5">
                      <i className="fas fa-clipboard-list fa-3x text-muted mb-3 d-block"></i>
                      <p className="text-muted mb-0">Nenhum log encontrado</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Paginação */}
        {paginacao.totalPaginas > 1 && (
          <div className="card-footer bg-transparent">
            <div className="d-flex justify-content-between align-items-center">
              <small className="text-muted">
                Mostrando {((paginacao.pagina - 1) * paginacao.limite) + 1} a{' '}
                {Math.min(paginacao.pagina * paginacao.limite, paginacao.total)} de{' '}
                {paginacao.total} registros
              </small>
              <nav>
                <ul className="pagination pagination-sm mb-0">
                  <li className={`page-item ${paginacao.pagina === 1 ? 'disabled' : ''}`}>
                    <button 
                      className="page-link" 
                      onClick={() => setPaginacao(prev => ({ ...prev, pagina: prev.pagina - 1 }))}
                    >
                      Anterior
                    </button>
                  </li>
                  {Array.from({ length: Math.min(5, paginacao.totalPaginas) }, (_, i) => {
                    const pageNum = i + 1;
                    return (
                      <li key={pageNum} className={`page-item ${paginacao.pagina === pageNum ? 'active' : ''}`}>
                        <button 
                          className="page-link" 
                          onClick={() => setPaginacao(prev => ({ ...prev, pagina: pageNum }))}
                        >
                          {pageNum}
                        </button>
                      </li>
                    );
                  })}
                  <li className={`page-item ${paginacao.pagina === paginacao.totalPaginas ? 'disabled' : ''}`}>
                    <button 
                      className="page-link" 
                      onClick={() => setPaginacao(prev => ({ ...prev, pagina: prev.pagina + 1 }))}
                    >
                      Próximo
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
        )}
      </div>

      {/* Modal de Detalhes */}
      {showModal && logSelecionado && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="fas fa-file-alt me-2"></i>
                  Detalhes do Log #{logSelecionado.id}
                </h5>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="row g-4">
                  <div className="col-md-6">
                    <table className="table table-sm">
                      <tbody>
                        <tr>
                          <th style={{ width: '40%' }}>ID:</th>
                          <td>{logSelecionado.id}</td>
                        </tr>
                        <tr>
                          <th>Data/Hora:</th>
                          <td>{formatarDataHora(logSelecionado.created_at)}</td>
                        </tr>
                        <tr>
                          <th>Administrador:</th>
                          <td>{logSelecionado.admin_nome || `Admin #${logSelecionado.admin_id}`}</td>
                        </tr>
                        <tr>
                          <th>Ação:</th>
                          <td>
                            <span className={`badge bg-${getAcaoCor(logSelecionado.acao)}`}>
                              <i className={`fas ${getAcaoIcone(logSelecionado.acao)} me-1`}></i>
                              {logSelecionado.acao}
                            </span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div className="col-md-6">
                    <table className="table table-sm">
                      <tbody>
                        <tr>
                          <th style={{ width: '40%' }}>Entidade:</th>
                          <td>{logSelecionado.entidade}</td>
                        </tr>
                        <tr>
                          <th>ID Entidade:</th>
                          <td>{logSelecionado.entidade_id || '-'}</td>
                        </tr>
                        <tr>
                          <th>IP:</th>
                          <td><code>{logSelecionado.ip}</code></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {logSelecionado.detalhes && (
                  <div className="mt-3">
                    <h6 className="text-muted mb-2">Detalhes</h6>
                    {formatarDetalhes(logSelecionado.detalhes)}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminAuditoria;
